"use client";

/**
 * BBLogoLoader — full-screen loading splash with the vectorized BB logo.
 *
 * Replaces the white flash you saw between Firebase auth resolution and
 * the AppShell painting. Uses the theme's own background + foreground
 * tokens so it lands clean inside every theme (dark / light / sepia /
 * per-user custom) — no hardcoded colours.
 *
 * CSS variables let Super Admin → Themes → Loading Screen tune the look
 * without redeploying:
 *
 *   --bb-loader-bg         hsl(var(--background))
 *   --bb-loader-fg         hsl(var(--foreground))
 *   --bb-loader-accent     hsl(var(--primary))
 *   --bb-loader-pulse-sec  2.4
 *   --bb-loader-spin-sec   2.6
 *   --bb-loader-scale      1     (overall size multiplier)
 *
 * Anything published from the admin panel writes those CSS variables onto
 * the <html> element via the existing branding-context updater, so the
 * loader picks them up automatically.
 */

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useBranding } from "@/contexts/branding-context";
import { getRandomContentThumbSync, getThumbMeta, loadContentThumbs } from "@/lib/content-thumbs";
import Link from "next/link";

interface Props {
  /** Smaller animation for a section loading inside an otherwise usable page. */
  compact?: boolean;
  /** Scale the circle to the available hero bounds. */
  hero?: boolean;
  /** Optional subtitle below the logo — defaults to "Breakin' Bread". */
  label?: string;
  /** Hide the wordmark and show just the logo (useful in small contexts). */
  iconOnly?: boolean;
  className?: string;
  /** "fixed" (default) = pinned to viewport for the auth splash.
   *  "embed" = fills its parent — used by the admin preview tile. */
  mode?: "fixed" | "embed";
}

export function BBLogoLoader({ label = "Breakin' Bread", iconOnly = false, className, mode = "fixed", compact = false, hero = false }: Props) {
  // Pull the admin-uploaded BB logo so the loader animates around the
  // real graffiti "B" mark instead of a stock SVG monogram. The context
  // always resolves logoUrl (admin upload → hosted default PNG), so the
  // real logo spins from the first paint. The old code read
  // settings.logoUrl (empty until the Firestore snapshot arrives) and
  // fell back to /bb-logo.svg — a file that never existed in public/ —
  // so the cold-boot splash spun an empty broken image.
  const branding = useBranding();
  const logoUrl: string = (branding as any)?.logoUrl || "";
  // Offline/error fallback: bundled single-colour vectors — black for
  // light theme, white for dark (each invisible on the other's bg).
  const [logoFailed, setLogoFailed] = useState(false);
  // A RANDOM content thumbnail spins instead of the BB logo (Gordon).
  // Restore cached artwork after hydration so server and browser markup match.
  // The bundled logo remains visible while the shared thumbnail pool warms.
  const [thumb, setThumb] = useState<string | null>(null);
  const [thumbFailed, setThumbFailed] = useState(false);
  useEffect(() => {
    setThumb(cur => cur || getRandomContentThumbSync());
    loadContentThumbs().then(() => {
      setThumb(cur => cur || getRandomContentThumbSync());
    }).catch(() => {});
  }, []);
  const spinSrc = !thumbFailed && thumb ? thumb : (!logoFailed && logoUrl ? logoUrl : "");
  const spinIsThumb = !thumbFailed && !!thumb;
  // No app title under the spinner — the spinning artwork's own title, linked
  // to its page (Gordon 2026-10-30: "a random selection while loading").
  const spinMeta = spinIsThumb ? getThumbMeta(thumb) : null;
  return (
    <div
      className={cn(
        mode === "fixed" ? "fixed inset-0 z-[200]" : "absolute inset-0",
        "flex flex-col items-center justify-center select-none",
        className,
      )}
      style={{
        containerType: hero ? "size" : undefined,
        background: "var(--bb-loader-bg, hsl(var(--background)))",
        color: "var(--bb-loader-fg, hsl(var(--foreground)))",
      }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {/* The actual app logo (admin-uploaded branding mark) spinning around
          the Z axis, with a subtle pulse ring behind it. Per Gordon: "logo
          should spin around the z axis ... same as app logo." Background is
          the theme background (set on the wrapper above), so the loader
          always matches the active theme instead of a hard-coded colour. */}
      {/* Vinyl-label scale (Gordon): near-full-width on mobile with a little
          padding, LP-label huge on desktop — bumped larger 2026-09-10
          ("larger load page animation"). --bb-loader-scale (Super Admin →
          Themes → Loading Screen → Size) multiplies the whole animation. */}
      <div
        // Phones: a calmer 60vw disc that the admin Size setting can only
        // shrink, never grow (Gordon 2026-10-29: "loading animation on mobile
        // little too large now"); sm+ keeps the full admin scale.
        className={cn("relative flex items-center justify-center", hero ? "bb-loader-hero-disc aspect-square shrink-0" : compact ? "size-20" : "bb-loader-disc w-[min(60vw,15rem)] h-[min(60vw,15rem)] sm:w-[min(54vw,30rem)] sm:h-[min(54vw,30rem)] lg:w-[min(42vw,34rem)] lg:h-[min(42vw,34rem)]")}
      >
        {/* Subtle pulse ring */}
        <svg viewBox="0 0 100 100" className="absolute inset-0">
          <circle
            cx="50" cy="50" r="46"
            fill="none"
            stroke="var(--bb-loader-accent, hsl(var(--primary)))"
            strokeWidth="1.5"
            opacity="0.3"
          >
            {/* Pulses just around the artwork edge — the spinning thumbnail is
                the same diameter as this ring (Gordon: "random image should be
                same size as the outline circle"). */}
            <animate attributeName="r" values="44;49;44" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;0;0.5" dur="2.4s" repeatCount="indefinite" />
          </circle>
        </svg>
        {/* Spinning artwork — a random content thumbnail like a vinyl label
            (falls back to the app logo while the pool warms up). */}
        {spinSrc ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={spinSrc}
            alt={label}
            onError={() => { if (spinIsThumb) setThumbFailed(true); else setLogoFailed(true); }}
            className={cn(
              // Thumbnail matches the outline circle exactly (ring r=46 in a
              // 100-unit viewBox → 92% diameter); the logo keeps contain-fit.
              spinIsThumb ? "w-[92%] h-[92%] object-cover rounded-full shadow-2xl" : "w-[62%] h-[62%] object-contain",
            )}
            style={{ animation: "bb-loader-spin var(--bb-loader-spin-sec, 2.6s) linear infinite", transformOrigin: "50% 50%" }}
          />
        ) : (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/brand/bb-logo.svg"
              alt={label}
              className="w-[62%] h-[62%] object-contain block dark:hidden"
              style={{ animation: "bb-loader-spin var(--bb-loader-spin-sec, 2.6s) linear infinite", transformOrigin: "50% 50%" }}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/brand/bb-logo-white.svg"
              alt={label}
              className="w-[62%] h-[62%] object-contain hidden dark:block"
              style={{ animation: "bb-loader-spin var(--bb-loader-spin-sec, 2.6s) linear infinite", transformOrigin: "50% 50%" }}
            />
          </>
        )}
      </div>

      {!iconOnly && spinMeta && (
        <Link
          href={spinMeta.href}
          className="mt-5 max-w-[80vw] truncate text-sm font-medium tracking-wide uppercase opacity-80 hover:opacity-100 hover:underline underline-offset-4"
          style={{ animation: "bb-loader-fadein 0.6s ease-out 0.4s both" }}
        >
          {spinMeta.title}
        </Link>
      )}

      {/* Scoped keyframes — no global CSS file touched. */}
      <style>{`
        @keyframes bb-loader-draw       { to { stroke-dashoffset: 0; } }
        @keyframes bb-loader-fadein     { to { opacity: 1; } }
        @keyframes bb-loader-spin       { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes bb-loader-logo-pulse {
          0%, 100% { transform: scale(0.96); opacity: 0.95; }
          50%      { transform: scale(1.04); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
